import { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* [SAGE-JADE 2026-09-28] Native controls (date pickers, selects, checkboxes, scrollbars) follow the
     app theme; without this they stayed light-themed inside dark mode. */
  :root {
    color-scheme: ${(p) => p.theme.colorScheme};
  }

  /* [SAGE-JADE] One keyboard-focus ring for anything that does not style its own focus. Components
     that set their own :focus / :focus-visible rule are more specific and keep theirs. */
  :focus-visible {
    outline: 2px solid ${(p) => p.theme.focus};
    outline-offset: 2px;
  }

  ::selection {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,162,113,0.40)' : 'rgba(41,163,131,0.24)')};
    color: ${(p) => p.theme.text};
  }

  /* [FLUID-5 2026-10-12, user] The row wave (components/Fluid.tsx landRows): each visible row rises 6 px and fades in at
     its own delay, top to bottom. Fill mode backwards, so a landed row keeps its own hover / selection / flash styles.
     Two identical keyframes, picked by the wave's token (…a / …b), so a new wave on the same rows restarts cleanly.
     --fl-land-y: a table's last row starts at 0 (fade only), so nothing ever hangs below the table (no scrollbar). */
  @keyframes flRowRiseA { from { opacity: var(--fl-land-o, 0); transform: translateY(var(--fl-land-y, 6px)); } to { opacity: 1; transform: none; } }
  @keyframes flRowRiseB { from { opacity: var(--fl-land-o, 0); transform: translateY(var(--fl-land-y, 6px)); } to { opacity: 1; transform: none; } }
  tbody tr[data-fl-land] {
    animation-duration: var(--fl-land-rise, 260ms);
    animation-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
    animation-fill-mode: backwards;
    animation-delay: var(--fl-land-delay, 0ms);
  }
  tbody tr[data-fl-land$='a'] { animation-name: flRowRiseA; }
  tbody tr[data-fl-land$='b'] { animation-name: flRowRiseB; }
  @media (prefers-reduced-motion: reduce) {
    tbody tr[data-fl-land] { animation: none; }
  }

  /* [SEARCH-HIGHLIGHT 2026-09-29, user] The searched text inside table rows (hooks/useSearchHighlight),
     in the amber tint the user picked (template option 4). */
  ::highlight(dt-search) {
    background-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(245, 158, 11, 0.26)' : 'rgba(245, 158, 11, 0.22)')};
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#ffd9a0' : '#6b3a04')};
  }

  html, body {
    height: 100%;
    font-family: 'Plus Jakarta Sans', system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
    color: ${(props) => props.theme.text};
    background: ${(props) => props.theme.bg};
    font-size: 14px;
    transition: background-color 320ms cubic-bezier(0.4, 0, 0.2, 1), color 320ms cubic-bezier(0.4, 0, 0.2, 1);

    /* Crisper text rendering so glass surfaces don't muddy the label edges. */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  /* Accessibility + battery: honor the OS's reduced-motion preference. The
     theme-toggle override above sets transition: none with !important during
     swaps, so this rule layers cleanly above it. */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* During a theme swap, suppress per-element transitions so colored table
     rows / panels snap to the new theme instantly. Only the body cross-fades
     (handled by the rule above) — that single, page-wide fade hides the
     snap of all the smaller surfaces and avoids the per-row "scan line"
     animation cascade the user was seeing. */
  html[data-theme-transitioning="on"] *,
  html[data-theme-transitioning="on"] *::before,
  html[data-theme-transitioning="on"] *::after {
    transition: none !important;
    animation-duration: 0.001s !important;
    animation-delay: 0s !important;
  }

  /* The toggle knob is the exception: it keeps its slide so the sun-to-moon (or
     moon-to-sun) motion is visible while the body fades. [MOTION-C 2026-08-20] The
     curve was a spring that overshot its stop; at the user's decision it now glides
     to a halt on the shared emphasized curve. The MOTION is unchanged - only the
     bounce at the end is gone. (No backticks in a styled CSS comment.) */
  html[data-theme-transitioning="on"] [data-theme-knob],
  html[data-theme-transitioning="on"] [data-theme-knob]::before {
    transition: transform 420ms cubic-bezier(0.32, 0.72, 0, 1),
                background 280ms ease,
                box-shadow 280ms ease !important; /* [FLUID-8 2026-10-12] transform (was left) */
  }

  #root {
    height: 100%;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    font-family: inherit;
    cursor: pointer;
  }

  input, select {
    font-family: inherit;
  }

  /* Remove the up/down spinner arrows from every number input, app-wide. */
  input[type='number'] {
    -moz-appearance: textfield;
  }
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  /* Premium themed scrollbars — thin, rounded, translucent brand-green thumb on
     a transparent track. Replaces the chunky default bar that shows under wide
     tables (Refunds, Payments, …). The 2px transparent border + padding-box clip
     keeps the visible thumb slim (macOS-style) and floats it off the edge.
     Firefox uses scrollbar-width/color; WebKit/Chromium uses ::-webkit-scrollbar. */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(162,162,162,0.40)' : 'rgba(61,65,64,0.40)')} transparent;
  }

  *::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  *::-webkit-scrollbar-track {
    background: transparent;
  }
  *::-webkit-scrollbar-thumb {
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: padding-box;
    background-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(162,162,162,0.40)' : 'rgba(61,65,64,0.40)')};
    transition: background-color 180ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  *::-webkit-scrollbar-thumb:hover {
    background-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(162,162,162,0.65)' : 'rgba(61,65,64,0.65)')};
  }
  *::-webkit-scrollbar-corner {
    background: transparent;
  }

  /* ──────────────────────────────────────────────────────────────────────────
     MOBILE BASE (≤768px). Phone-focused refinements only — every rule lives
     inside a max-width query, so desktop/laptop rendering is byte-for-byte
     unchanged (these rules simply don't apply above the breakpoint).
     ────────────────────────────────────────────────────────────────────────── */
  @media (max-width: 768px) {
    /* [MOBILE 2026-07-08] Lock the DOCUMENT so the app-shell frame (top Header +
       bottom dock) can never be scrolled away — only the page content scrolls.
       Why this is needed: the Header is an in-flow grid row inside the Shell, while
       the dock is position:fixed. The Shell is sized 100dvh, but a mobile browser
       can still resolve that a few px TALLER than the truly-visible viewport during
       the URL-bar (dynamic-viewport) transition. With the body free to scroll, that
       few-px overflow becomes a real document scroll that drags the in-flow Header
       off the top — you'd see the top OR the bottom, never both. (Verified: a shell
       overshoot let the body scroll 78px and unpinned the header; overflow:hidden
       here made body-scroll 0 while the inner card still scrolled its full range, so
       no content is trapped.) height:100dvh keeps the root box == the visible
       viewport; overflow:hidden caps the scrollport to it so the frame stays put and
       the PageCard (its own overflow-y:scroll) is the only thing that moves.
       Safe for the three non-app screens (Login / No-access / loading): each is a
       position:fixed; inset:0 overlay, so it is neither clipped by this nor reliant
       on document scroll. Desktop is above the breakpoint and unaffected. */
    html, body {
      height: 100dvh;
      overflow: hidden;
    }
    /* iOS Safari zooms the viewport whenever a focused text field's font-size is
       < 16px, which knocks the whole layout sideways. Pin text inputs/areas to
       16px so tapping a field never triggers that zoom. */
    input:not([type='checkbox']):not([type='radio']):not([type='range']),
    textarea {
      font-size: 16px;
    }
    /* Kill the grey tap-flash on touch so buttons/links feel native. */
    * { -webkit-tap-highlight-color: transparent; }
  }
`;
